
import { getCachedToken, getOrCreateFolder } from './drive';

const DRIVE_FOLDER = 'cloudairambo_Invoices';
const DB_NAME = 'cloudairambo_db';
const STORE_NAME = 'app_data';

// ── File System Access API ────────────────────────────────────────────────────

let _dirHandle: FileSystemDirectoryHandle | null = null;

export function isFSASupported(): boolean {
  return 'showDirectoryPicker' in window;
}

export async function requestFolderPermission(): Promise<boolean> {
  try {
    _dirHandle = await (window as any).showDirectoryPicker({
      mode: 'readwrite',
      startIn: 'documents',
      id: 'cloudairambo',
    });
    // Save handle to IndexedDB so we can restore it next session
    await idbSave('__folder_handle__', _dirHandle);
    return true;
  } catch (e) {
    console.warn('User cancelled folder picker or not supported', e);
    return false;
  }
}

export async function restoreFolderPermission(): Promise<boolean> {
  try {
    const handle = await idbLoad('__folder_handle__');
    if (!handle) return false;
    // Check if we still have permission
    const perm = await handle.queryPermission({ mode: 'readwrite' });
    if (perm === 'granted') {
      _dirHandle = handle;
      return true;
    }
    // Request permission again
    const req = await handle.requestPermission({ mode: 'readwrite' });
    if (req === 'granted') {
      _dirHandle = handle;
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function isFolderConnected(): boolean {
  return _dirHandle !== null;
}

async function fsaSave(key: string, data: any): Promise<void> {
  if (!_dirHandle) throw new Error('No folder selected');
  const filename = `${key}.json`;
  const fileHandle = await _dirHandle.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(JSON.stringify(data, null, 2));
  await writable.close();
}

async function fsaLoad(key: string): Promise<any | null> {
  if (!_dirHandle) return null;
  try {
    const filename = `${key}.json`;
    const fileHandle = await _dirHandle.getFileHandle(filename);
    const file = await fileHandle.getFile();
    const text = await file.text();
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// ── IndexedDB (fallback) ──────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
    req.onerror = (e) => reject((e.target as IDBOpenDBRequest).error);
  });
}

async function idbSave(key: string, data: any): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({ key, data, updatedAt: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = (e) => reject((e.target as IDBRequest).error);
  });
}

async function idbLoad(key: string): Promise<any | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = (e) => {
      const result = (e.target as IDBRequest).result;
      resolve(result ? result.data : null);
    };
    req.onerror = (e) => reject((e.target as IDBRequest).error);
  });
}

// ── Google Drive ──────────────────────────────────────────────────────────────

async function saveJsonToDrive(token: string, filename: string, data: any): Promise<void> {
  const folderId = await getOrCreateFolder(token, DRIVE_FOLDER);
  const q = `name='${filename}' and '${folderId}' in parents and trashed=false`;
  const searchRes = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const searchData = await searchRes.json();
  const existingId = searchData.files?.[0]?.id;
  if (existingId) {
    await fetch(
      `https://www.googleapis.com/upload/drive/v3/files/${existingId}?uploadType=media`,
      {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
  } else {
    const metadata = { name: filename, parents: [folderId], mimeType: 'application/json' };
    const boundary = 'ca_boundary';
    const body =
      `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
      JSON.stringify(metadata) +
      `\r\n--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
      JSON.stringify(data) +
      `\r\n--${boundary}--`;
    await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body,
    });
  }
}

async function loadJsonFromDrive(token: string, filename: string): Promise<any | null> {
  try {
    const folderId = await getOrCreateFolder(token, DRIVE_FOLDER);
    const q = `name='${filename}' and '${folderId}' in parents and trashed=false`;
    const searchRes = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const searchData = await searchRes.json();
    const fileId = searchData.files?.[0]?.id;
    if (!fileId) return null;
    const fileRes = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return await fileRes.json();
  } catch {
    return null;
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

export const StorageKeys = {
  INVOICES:    'cloudairambo_invoices',
  CLIENTS:     'cloudairambo_clients',
  LOGS:        'cloudairambo_logs',
  RECON_RULES: 'cloudairambo_recon_rules',
  BANK_TXNS:   'cloudairambo_bank_transactions',
  PAYROLL:     'cloudairambo_payroll_employees',
};

export async function saveData(key: string, data: any): Promise<void> {
  // 1. Save to HDD folder if connected
  if (_dirHandle) {
    try { await fsaSave(key, data); } 
    catch (e) { console.warn('FSA save failed', e); }
  } else {
    // 2. Fall back to IndexedDB
    try { await idbSave(key, data); } 
    catch (e) { console.warn('IndexedDB save failed', e); }
  }

  // 3. Always sync to Drive in background if connected
  const token = getCachedToken();
  if (token) {
    try { await saveJsonToDrive(token, `${key}.json`, data); } 
    catch (e) { console.warn('Drive sync failed', e); }
  }
}

export async function loadData(key: string): Promise<any | null> {
  // 1. Try HDD folder first
  if (_dirHandle) {
    try {
      const data = await fsaLoad(key);
      if (data !== null) return data;
    } catch (e) { console.warn('FSA load failed', e); }
  }

  // 2. Try Drive if connected
  const token = getCachedToken();
  if (token) {
    try {
      const data = await loadJsonFromDrive(token, `${key}.json`);
      if (data !== null) return data;
    } catch (e) { console.warn('Drive load failed', e); }
  }

  // 3. Fall back to IndexedDB
  try {
    const data = await idbLoad(key);
    if (data !== null) return data;
  } catch (e) { console.warn('IndexedDB load failed', e); }

  return null;
}

export function isDriveConnected(): boolean {
  return !!getCachedToken();
}
